import { z } from "zod";
import { ALL_STATS, STAT_KEYS } from "./stats.js";
import { MAX_PERIODS, REGULATION_PERIODS, halfLineProblem, lineScoreProblem, } from "./lineScore.js";
/** A stat value: nullable everywhere, because a manager may submit a result
 * with only a partial stat line (or none at all — score-only is legal). */
const statValue = (negative, half) => {
    let base = z.number().finite().max(100_000);
    if (!negative)
        base = base.min(0, "cannot be negative");
    if (!half)
        base = base.int("must be a whole number");
    const checked = half
        ? base.refine((v) => Math.abs(v * 2 - Math.round(v * 2)) < 1e-9, {
            message: "must be a whole or half value",
        })
        : base;
    return checked.nullable().optional();
};
export const statLineSchema = z
    .object(Object.fromEntries(ALL_STATS.map((s) => [
    s.key,
    s.kind === "duration"
        ? z.number().int().min(0).max(24 * 3600).nullable().optional()
        : statValue(Boolean(s.negative), s.kind === "half"),
])))
    .strict()
    .superRefine((line, ctx) => {
    // Cheap sanity checks that catch the common mis-entry: a "made" that
    // exceeds its "attempted". Not exhaustive, just the pairs that matter.
    const pairs = [
        ["pass_cmp", "pass_att", "Completions"],
        ["third_down_conv", "third_down_att", "3rd down conversions"],
        ["fourth_down_conv", "fourth_down_att", "4th down conversions"],
        ["two_pt_conv", "two_pt_att", "2pt conversions"],
        ["fgm", "fga", "Field goals made"],
        ["xpm", "xpa", "Extra points made"],
    ];
    for (const [made, att, label] of pairs) {
        const m = line[made];
        const a = line[att];
        if (m != null && a != null && Number(m) > Number(a)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [made],
                message: `${label} cannot exceed attempts`,
            });
        }
    }
});
export const emptyStatLine = () => Object.fromEntries(STAT_KEYS.map((k) => [k, null]));
// ---------------------------------------------------------------------------
export const gameStageSchema = z.enum(["regular", "playoff", "tournament"]);
export const gameStatusSchema = z.enum([
    "scheduled",
    "pending_confirmation",
    "final",
    "disputed",
]);
export const managerRoleSchema = z.enum(["commissioner", "player"]);
export const articleStatusSchema = z.enum(["draft", "published"]);
/** One side of a submitted result. */
export const sideResultSchema = z.object({
    managerId: z.number().int().positive(),
    teamAbbr: z.string().min(1).max(8).nullable().optional(),
    score: z.number().int().min(0).max(200),
    stats: statLineSchema.nullable().optional(),
});
/**
 * The four teams taken off the board before a game, in ban order.
 *
 * Rev 2.0's ban phase removes four *different* franchises, so the same team
 * twice is not a legal election — and nothing downstream expects it: one
 * franchise view counted ban slots and the other counted games, so a duplicate
 * made the two halves of the Teams page disagree about the same team.
 */
const banListSchema = z
    .array(z.string().min(1).max(8))
    .max(4)
    .refine((bans) => new Set(bans.map((b) => b.trim().toUpperCase())).size === bans.length, { message: "The same team can't be banned twice in one game." });
/**
 * The quarter-by-quarter breakdown, optional on every path that carries a
 * score. Null clears one already recorded; leaving it out entirely leaves what
 * is there alone.
 *
 * The sums are checked against the reported score by the schema that owns both
 * — see `lineScoreProblem`, which the entry form runs too so the message lands
 * next to the boxes.
 */
export const lineScoreSchema = z.object({
    home: z
        .array(z.number().int().min(0).max(200))
        .min(REGULATION_PERIODS)
        .max(MAX_PERIODS),
    away: z
        .array(z.number().int().min(0).max(200))
        .min(REGULATION_PERIODS)
        .max(MAX_PERIODS),
});
/**
 * The same breakdown as `lineScoreSchema`, but either side may be missing.
 *
 * A manager filing their own half has their own quarters and, often, nothing
 * for the other column — so a report carries what it has and the game's own
 * line score is assembled from the two (see `mergeLineScores`).
 */
export const partialLineScoreSchema = z.object({
    home: z
        .array(z.number().int().min(0).max(200))
        .min(REGULATION_PERIODS)
        .max(MAX_PERIODS)
        .nullable()
        .optional(),
    away: z
        .array(z.number().int().min(0).max(200))
        .min(REGULATION_PERIODS)
        .max(MAX_PERIODS)
        .nullable()
        .optional(),
});
/** Attach the cross-field line score checks to a schema that has the scores. */
const checkLineScore = (line, homeScore, awayScore, ctx) => {
    if (!line)
        return;
    const problem = lineScoreProblem(line, homeScore, awayScore);
    if (problem) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["lineScore"],
            message: problem,
        });
    }
};
export const submitResultSchema = z
    .object({
    gameId: z.number().int().positive(),
    playedAt: z.string().datetime().nullable().optional(),
    home: sideResultSchema,
    away: sideResultSchema,
    notes: z.string().max(2000).nullable().optional(),
    /**
     * The pre-game election, same as `submitSideSchema`.
     *
     * The form has always shown these on the Commissioner's path but the
     * payload didn't carry them, so they were silently dropped — which is why
     * Season 1's pick order needed a separate backfill screen.
     */
    firstPickManagerId: z.number().int().positive().nullable().optional(),
    bans: banListSchema.optional(),
    /** quarter by quarter, adding up to the two side scores above */
    lineScore: lineScoreSchema.nullable().optional(),
})
    .superRefine((v, ctx) => checkLineScore(v.lineScore, v.home.score, v.away.score, ctx))
    .refine((v) => v.home.managerId !== v.away.managerId, {
    message: "A manager cannot play themselves",
    path: ["away", "managerId"],
})
    .refine((v) => !v.home.teamAbbr ||
    !v.away.teamAbbr ||
    v.home.teamAbbr.toUpperCase() !== v.away.teamAbbr.toUpperCase(), {
    // Rev 2.0: "Mirror matches (both players playing the same team in the
    // same match) is not permitted"
    message: "Mirror matches are not permitted — both managers picked the same team",
    path: ["away", "teamAbbr"],
});
/**
 * One manager's own half of a game: their points, their team, their quarters
 * and their stat line — plus whatever they know about the other side.
 *
 * Both managers file one of these, in either order and without waiting on each
 * other. Your own side's score is the only part that is required; the
 * opponent's is optional, because the two of you are usually typing at the same
 * time. The game is final once both halves are in, and a side that both of you
 * put a number against, differently, is a dispute.
 *
 * Which side is "yours" depends on who is signed in, so the server enforces it
 * — the schema only insists that a report says something about the score.
 */
export const submitSideSchema = z
    .object({
    gameId: z.number().int().positive(),
    /**
     * The score as this manager reports it. Their own side is required; the
     * other may be null when they are only filing their own half.
     */
    homeScore: z.number().int().min(0).max(200).nullable().optional(),
    awayScore: z.number().int().min(0).max(200).nullable().optional(),
    /** this manager's own team selection */
    teamAbbr: z.string().min(1).max(8).nullable().optional(),
    /**
     * Which team the opponent played as.
     *
     * You know what you just played against, and there is no reason to make the
     * franchise record wait on them to log in — so either manager can record it.
     * Their own entry still wins: this is only written while they haven't
     * reported the game themselves.
     */
    opponentTeamAbbr: z.string().min(1).max(8).nullable().optional(),
    /** this manager's own stat line */
    stats: statLineSchema.nullable().optional(),
    /**
     * The opponent's stat line, when you have the box score and they don't.
     * Optional, and never a substitute for their own half — they still have to
     * file it before the game is final. Ignored if they have already entered
     * their own line.
     */
    opponentStats: statLineSchema.nullable().optional(),
    playedAt: z.string().datetime().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    /**
     * The pre-game election, recorded by whoever submits it first.
     *
     * `bans` is just the four teams in the order they were banned — who banned
     * each one follows from `firstPickManagerId` under Rev 2.0 and is derived,
     * not entered.
     */
    firstPickManagerId: z.number().int().positive().nullable().optional(),
    bans: banListSchema.optional(),
    /**
     * Quarter by quarter, adding up to the score reported above — one side of
     * it or both, matching how much of the game this report covers.
     */
    lineScore: partialLineScoreSchema.nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.homeScore == null && v.awayScore == null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["homeScore"],
            message: "Put in at least your own score.",
        });
    }
    // Each side's quarters are checked against that side's score on its own:
    // half a report has nothing to compare across the two columns.
    for (const side of ["home", "away"]) {
        const periods = v.lineScore?.[side];
        if (!periods)
            continue;
        const problem = halfLineProblem(periods, v[`${side}Score`] ?? null);
        if (problem) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["lineScore"],
                message: problem,
            });
        }
    }
    // A report that does cover both columns gets the cross-side checks too —
    // equal periods, and an overtime only where regulation finished level.
    if (v.lineScore?.home && v.lineScore.away) {
        checkLineScore({ home: v.lineScore.home, away: v.lineScore.away }, v.homeScore ?? null, v.awayScore ?? null, ctx);
    }
})
    .refine((v) => !v.teamAbbr ||
    !v.opponentTeamAbbr ||
    v.teamAbbr.toUpperCase() !== v.opponentTeamAbbr.toUpperCase(), {
    // Rev 2.0 again: the same franchise on both sides is not a legal game,
    // and here one person is typing both halves of it.
    message: "Mirror matches are not permitted — that's the team you picked.",
    path: ["opponentTeamAbbr"],
});
export const loginSchema = z.object({
    username: z.string().min(1).max(64),
    password: z.string().min(1).max(200),
});
export const articleInputSchema = z.object({
    id: z.number().int().positive().optional(),
    title: z.string().min(1).max(300),
    subtitle: z.string().max(500).nullable().optional(),
    slug: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "lowercase words separated by hyphens"),
    body: z.string().min(1),
    seasonId: z.number().int().positive().nullable().optional(),
    status: articleStatusSchema,
    tags: z.array(z.string().max(40)).max(12).optional(),
    publishedAt: z.string().datetime().nullable().optional(),
});
export function slugify(input) {
    return input
        .toLowerCase()
        .replace(/['’]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 200);
}
//# sourceMappingURL=schemas.js.map